import { api } from "@/lib/api/client";
import { partnerEndpoints } from "@/lib/api/endpoints";

export type IdNameOption = { id: number; name: string };
export type ValueLabelOption = { value: string; label: string };

export type BusinessKyc = {
  id: number;
  uuid: string;
  name: string | null;
  status: string;
  status_display: string;
  can_edit: boolean;
  address: string | null;
  city: number | null;
  city_name: string | null;
  postal_code: string | null;
  business_type: number | null;
  business_type_name: string | null;
  business_segment: number | null;
  business_segment_name: string | null;
  industry_sector: number | null;
  industry_sector_name: string | null;
  identity_type: string | null;
  identity: string | null;
  verified_at: string | null;
};

export type BusinessKycPayload = {
  name: string;
  address?: string;
  city?: number | null;
  postal_code?: string;
  business_type?: number | null;
  business_segment?: number | null;
  industry_sector?: number | null;
  identity_type?: string;
  identity?: string;
};

export type PersonalKyc = {
  id: number;
  uuid: string;
  name: string | null;
  nationality: number | null;
  nationality_name: string | null;
  passport_no: string | null;
  is_uae_resident: boolean;
  identity: string | null;
  identity_type: string | null;
  address: string | null;
  city: number | null;
  postal_code: string | null;
  shareholder: number | null;
  status: string;
  status_display: string;
  can_edit: boolean;
  remarks: string | null;
};

export type PersonalKycPayload = {
  name?: string;
  nationality?: number | null;
  passport_no?: string | null;
  is_uae_resident?: boolean;
  identity?: string | null;
};

export type ShareholderType = "INDIVIDUAL" | "BUSINESS";

export type Shareholder = {
  id: number;
  uuid: string;
  name: string;
  email?: string | null;
  phone_number?: string | null;
  shareholding_percentage: number | null;
  shareholder_type: ShareholderType;
  shareholder_type_display: string;
  identity_type?: string | null;
  identity?: string | null;
  status: string;
  is_business_owner: boolean;
  kyc_required: boolean;
  personal_kyc: PersonalKyc | null;
  /** UUID of the top-level BUSINESS shareholder this row holds shares in, or
   * null when this row itself sits directly under the borrower company. */
  business_shareholder: string | null;
};

export type ShareholderPayload = {
  name: string;
  shareholder_type: ShareholderType;
  shareholding_percentage: number;
  is_business_owner: boolean;
  email?: string;
  /** UUID of the parent BUSINESS shareholder this row holds shares in, or
   * null/omitted for a top-level holder of the borrower company. */
  business_shareholder?: string | null;
};

export type KycReferenceData = {
  cities: IdNameOption[];
  business_types: IdNameOption[];
  business_segments: IdNameOption[];
  industry_sectors: IdNameOption[];
  identity_types: ValueLabelOption[];
  nationalities: IdNameOption[];
};

export type KycOverview = {
  business_kyc: BusinessKyc | null;
  shareholders: Shareholder[];
};

export async function getKycReference(): Promise<KycReferenceData> {
  return api.get<KycReferenceData>(partnerEndpoints.kycReference);
}

export async function getKycOverview(applicationUuid: string): Promise<KycOverview> {
  return api.get<KycOverview>(partnerEndpoints.kycOverview(applicationUuid));
}

export async function createBusinessKyc(
  applicationUuid: string,
  payload: BusinessKycPayload,
): Promise<BusinessKyc> {
  return api.post<BusinessKyc>(partnerEndpoints.businessKycCreate(applicationUuid), payload);
}

export async function updateBusinessKyc(
  kycUuid: string,
  payload: BusinessKycPayload,
): Promise<BusinessKyc> {
  return api.patch<BusinessKyc>(partnerEndpoints.businessKycUpdate(kycUuid), payload);
}

export async function listShareholders(applicationUuid: string): Promise<Shareholder[]> {
  const data = await api.get<Shareholder[] | { results: Shareholder[] }>(
    partnerEndpoints.shareholders(applicationUuid),
  );
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
}

export async function createShareholder(
  applicationUuid: string,
  payload: ShareholderPayload,
): Promise<Shareholder> {
  return api.post<Shareholder>(partnerEndpoints.shareholders(applicationUuid), payload);
}

export async function updateShareholder(
  shareholderUuid: string,
  payload: Partial<ShareholderPayload>,
): Promise<Shareholder> {
  return api.patch<Shareholder>(partnerEndpoints.shareholder(shareholderUuid), payload);
}

export async function deleteShareholder(shareholderUuid: string): Promise<void> {
  await api.delete(partnerEndpoints.shareholder(shareholderUuid));
}

export async function createPersonalKyc(
  shareholderUuid: string,
  payload: PersonalKycPayload = {},
): Promise<PersonalKyc> {
  return api.post<PersonalKyc>(partnerEndpoints.personalKycCreate(shareholderUuid), payload);
}

export async function updatePersonalKyc(
  kycUuid: string,
  payload: PersonalKycPayload,
): Promise<PersonalKyc> {
  return api.patch<PersonalKyc>(partnerEndpoints.personalKycUpdate(kycUuid), payload);
}
